import { createClient } from '@supabase/supabase-js';

export interface HabiSelection {
  organizationId: string;
  actorId: string;
  configurationId: string;
  colleagueId: string;
  chatJid: string;
}
export interface AssignmentSnapshot {
  assigned_operator_id: string | null;
  assigned_at: string | null;
  updated_at: string | null;
}
export interface HabiAccess {
  connected: boolean;
  self: boolean;
  colleague: boolean;
  canAssign: boolean;
}
export interface AssignmentProposal {
  id: string;
  selection: Readonly<HabiSelection>;
  before: Readonly<AssignmentSnapshot> | null;
  expiresAt: number;
}
export type AssignmentResult = 'verified' | 'denied' | 'conflict' | 'unknown' | 'expired' | 'cancelled' | 'invalid';

/** DEV-only until a server mutation endpoint enforces ALL these guards atomically.
 * Database policy currently protects organization membership, not action permissions.
 * No default URL and no production fallback. The client is created only for the explicit DEV origin, using the current user JWT.
 */
export function createHabiRepository(backendUrl: string, anonKey: string, getJwt: () => Promise<string | null>, now = Date.now) {
  if (new URL(backendUrl).origin !== 'https://dev-sb.flusia.it') throw new Error('Habi pilot requires DEV Supabase');
  const client=createClient(backendUrl,anonKey,{
    auth:{persistSession:false,autoRefreshToken:false,detectSessionInUrl:false},
    global:{fetch:async(input,init)=>{
      const url=typeof input==='string'?input:input instanceof URL?input.href:input.url;
      if(new URL(url).origin!=='https://dev-sb.flusia.it')throw new Error('Unexpected backend');
      const jwt=await getJwt();if(!jwt)throw new Error('Session missing');
      const headers=new Headers(init?.headers);headers.set('Authorization',`Bearer ${jwt}`);
      return fetch(input,{...init,headers,redirect:'error'});
    }},
  });
  const getUser=async()=>{const jwt=await getJwt();if(!jwt)throw new Error('Session missing');return client.auth.getUser(jwt);};
  const proposals = new Map<string, AssignmentProposal>();
  const results = new Map<string, AssignmentResult>();
  const running = new Map<string, Promise<AssignmentResult>>();
  const key = (s: HabiSelection) => JSON.stringify([s.organizationId,s.actorId,s.configurationId,s.colleagueId,s.chatJid]);
  const validate = (s: HabiSelection) => {
    const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    if (![s.organizationId,s.actorId,s.configurationId,s.colleagueId].every(v=>uuid.test(v)) ||
        s.actorId === s.colleagueId || !/^[0-9A-Za-z._:-]{1,160}@(c\.us|s\.whatsapp\.net|g\.us|lid)$/.test(s.chatJid)) throw new Error('Invalid selection');
  };
  const identity = async (s: HabiSelection) => {
    validate(s);
    const {data,error}=await getUser();
    if(error || data.user?.id !== s.actorId) throw new Error('Session changed');
    const profile = await client.from('user_profiles').select('id').eq('id',s.actorId).eq('organization_id',s.organizationId).eq('active',true).single();
    if(profile.error || !profile.data) throw new Error('Actor unavailable');
  };
  const access = async (s: HabiSelection): Promise<HabiAccess> => {
    await identity(s);
    const [cfg, users, acl, permission, disabled, globallyDisabled, viewPermission] = await Promise.all([
      client.from('api_configurations').select('id,service_name,is_active,visibility,user_id,session_health_status,webhook_subscribed_at')
        .eq('organization_id',s.organizationId).eq('id',s.configurationId).in('service_name',['waha','whatsapp_cloud','whatsapp_cloud_coex']).single(),
      client.from('user_profiles').select('id').eq('organization_id',s.organizationId).eq('id',s.colleagueId).eq('active',true).maybeSingle(),
      client.from('whatsapp_user_access').select('user_id').eq('organization_id',s.organizationId).eq('configuration_id',s.configurationId),
      client.rpc('current_user_has_permission',{p_resource:'whatsapp',p_action:'update'}),
      client.rpc('get_organization_disabled_permissions'),
      client.rpc('get_globally_disabled_features'),
      client.rpc('current_user_has_permission',{p_resource:'whatsapp',p_action:'view'}),
    ]);
    if(cfg.error || users.error || acl.error || permission.error || disabled.error || globallyDisabled.error || viewPermission.error || !cfg.data) throw new Error('Access check unavailable');
    const c=cfg.data;
    const ids=new Set((acl.data || []).map(row=>row.user_id));
    const allowed=(id:string)=> c.user_id === id || ((c.visibility === 'global' || !c.user_id) && (ids.size === 0 || ids.has(id)));
    return {
      connected: c.is_active === true && (c.service_name === 'waha' ? ['healthy','WORKING'].includes(c.session_health_status) : !!c.webhook_subscribed_at),
      self: allowed(s.actorId) && viewPermission.data === true &&
        !(disabled.data || []).some((p:{resource:string;action:string})=>p.resource==='whatsapp' && p.action==='view') &&
        !(globallyDisabled.data || []).some((p:{resource:string})=>p.resource==='whatsapp'), colleague: !!users.data && allowed(s.colleagueId), canAssign: permission.data === true &&
        !(disabled.data || []).some((p:{resource:string;action:string})=>p.resource==='whatsapp' && p.action==='update') &&
        !(globallyDisabled.data || []).some((p:{resource:string})=>p.resource==='whatsapp'),
    };
  };
  const binding = async (s: HabiSelection) => {
    // Verify only message existence for the selected number+chat. Never select content/raw_payload.
    const q=await client.from('whatsapp_messages').select('id').eq('organization_id',s.organizationId).eq('config_id',s.configurationId)
      .or(`from_jid.eq.${s.chatJid},to_jid.eq.${s.chatJid}`).limit(1);
    if(q.error || !q.data?.length) throw new Error('Conversation binding unavailable');
  };
  const read = async (s: HabiSelection): Promise<AssignmentSnapshot | null> => {
    const q=await client.from('whatsapp_chat_meta').select('assigned_operator_id,assigned_at,updated_at')
      .eq('organization_id',s.organizationId).eq('chat_jid',s.chatJid).maybeSingle();
    if(q.error) throw new Error('Assignment read unavailable');
    return q.data;
  };
  const ready = async (s: HabiSelection) => {
    const a=await access(s);
    if(!a.connected || !a.self || !a.colleague || !a.canAssign) throw new Error('Assignment denied');
    await binding(s);
  };
  // Durable evidence is the colleague's existing saved note, not a browser completion flag.
  // Owner verification proves activity, not that the other browser clicked a verification button.
  const activity=async(s:HabiSelection,meta:AssignmentSnapshot,noteId?:string)=>{
    if(!meta.assigned_at)throw new Error('Assignment timestamp missing');
      let noteQuery=client.from('whatsapp_chat_notes').select('id,created_by,created_at')
        .eq('organization_id',s.organizationId).eq('chat_jid',s.chatJid).eq('created_by',s.colleagueId)
        .gte('created_at',meta.assigned_at);
      if(noteId)noteQuery=noteQuery.eq('id',noteId);
      const note=await noteQuery.order('created_at',{ascending:false}).limit(1).maybeSingle();
      if(note.error || !note.data)throw new Error('Recipient activity not verified');
      const latest=await read(s);
      if(latest?.assigned_operator_id!==s.colleagueId || latest.assigned_at!==meta.assigned_at)throw new Error('Assignment changed during verification');
      return {noteId:note.data.id,configurationId:s.configurationId,chatJid:s.chatJid,recipientId:s.colleagueId,assignedAt:meta.assigned_at,verifiedAt:now()};
  };
  return {
    access,
    async readAssignment(s:HabiSelection) { const a=await access(s); if(!a.self)throw new Error('Number access denied'); await binding(s); return read(s); },
    async proposeAssignment(s:HabiSelection):Promise<AssignmentProposal> {
      await ready(s);
      const before=await read(s);
      const p=Object.freeze({id:crypto.randomUUID(),selection:Object.freeze({...s}),before:before ? Object.freeze({...before}) : null,expiresAt:now()+300_000});
      proposals.set(p.id,p); return p;
    },
    cancel(id:string) { if(!proposals.delete(id))return false;results.set(id,'cancelled');return true; },
    confirmAssignment(id:string, currentSelection:HabiSelection):Promise<AssignmentResult> {
      if(running.has(id))return running.get(id)!;
      if(results.has(id))return Promise.resolve(results.get(id)!);
      const p=proposals.get(id); if(!p)return Promise.resolve('invalid');proposals.delete(id);
      const run=async():Promise<AssignmentResult>=>{
        if(key(p.selection)!==key(currentSelection))return 'denied';
        if(now()>=p.expiresAt)return 'expired';
        try { await ready(p.selection); } catch { return 'denied'; }
        const s=p.selection;
        try {
          const current=await read(s);
          if(JSON.stringify(current)!==JSON.stringify(p.before))return 'conflict';
          if(current?.assigned_operator_id!==s.colleagueId){
            const patch={assigned_operator_id:s.colleagueId,assigned_at:new Date(now()).toISOString(),updated_at:new Date(now()).toISOString()};
            if(current){
              let q=client.from('whatsapp_chat_meta').update(patch).eq('organization_id',s.organizationId).eq('chat_jid',s.chatJid);
              q=current.updated_at===null?q.is('updated_at',null):q.eq('updated_at',current.updated_at);
              q=current.assigned_operator_id===null?q.is('assigned_operator_id',null):q.eq('assigned_operator_id',current.assigned_operator_id);
              const changed=await q.select('id');
              if(changed.error)return 'unknown';if(changed.data?.length!==1)return 'conflict';
            }else{
              // Insert, never upsert: a concurrent assignment must not be overwritten.
              const inserted=await client.from('whatsapp_chat_meta').insert({organization_id:s.organizationId,chat_jid:s.chatJid,...patch}).select('id');
              if(inserted.error)return inserted.error.code==='23505'?'conflict':'unknown';
              if(inserted.data?.length!==1)return 'unknown';
            }
          }
          await ready(s);
          return (await read(s))?.assigned_operator_id===s.colleagueId?'verified':'unknown';
        }catch{return 'unknown';}
      };
      const promise=run().then(r=>{results.set(id,r);running.delete(id);return r;});running.set(id,promise);return promise;
    },
    async verifyColleagueActivity(s:HabiSelection) {
      const a=await access(s);
      if(!a.connected || !a.self || !a.colleague)throw new Error('Shared access unavailable');
      await binding(s);
      const meta=await read(s);
      if(meta?.assigned_operator_id!==s.colleagueId || !meta.assigned_at)throw new Error('Assignment changed');
      return activity(s,meta);
    },
    /** Called while authenticated AS THE COLLEAGUE after they have saved an internal note.
     * Returns a proof for the controller only, never for the LLM. Does not create the note.
     * Owner independently rereads saved activity through verifyColleagueActivity.
     */
    async verifyRecipient(s:HabiSelection,noteId?:string) {
      validate(s);
      const session=await getUser();
      if(session.error || session.data.user?.id!==s.colleagueId)throw new Error('Recipient session required');
      const recipientSelection={...s,actorId:s.colleagueId,colleagueId:s.actorId};
      const a=await access(recipientSelection);
      if(!a.connected || !a.self)throw new Error('Recipient cannot access number');
      await binding(s);
      const meta=await read(s);
      if(meta?.assigned_operator_id!==s.colleagueId || !meta.assigned_at)throw new Error('Assignment changed');
      return activity(s,meta,noteId);
    },
  };
}
